import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface Resource {
  id: string;
  title: string;
  url: string | null;
  thumbnail: string | null;
  category: string;
  description: string | null;
  file_url: string | null;
  file_name: string | null;
  published_at: string | null;
  created_at: string;
}

export default function ResourcesSection() {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const categoryColors = {
    언론: 'bg-red-100 text-red-700',
    미디어: 'bg-blue-100 text-blue-700',
    캠페인: 'bg-teal-100 text-teal-700',
  };

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .order('published_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setResources(data || []);
    } catch (err) {
      console.error('자료실 데이터 로딩 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(fileUrl, '_blank');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleViewAll = () => {
    navigate('/resource');
  };

  return (
    <section id="resources" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 헤더 */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-100 rounded-full mb-4">
            <i className="ri-folder-open-line text-2xl text-orange-600"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">자료실</h2>
          <p className="text-gray-600 text-lg">직접 확인하고 판단하세요</p>
        </div>

        {/* 로딩 상태 */}
        {loading && (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-500">자료를 불러오는 중...</p>
          </div>
        )}

        {/* 게시물 그리드 */}
        {!loading && resources.length === 0 && (
          <div className="text-center py-20">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-lg">등록된 자료가 없습니다</p>
          </div>
        )}

        {!loading && resources.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {resources.map((resource) => (
                <div
                  key={resource.id}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  {/* 썸네일 */}
                  <div className="relative w-full h-48 bg-gray-100 overflow-hidden">
                    {resource.thumbnail ? (
                      <>
                        <img
                          src={resource.thumbnail}
                          alt={resource.title}
                          title={resource.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                        {resource.url && (
                          <div className="absolute top-3 right-3 w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <i className="ri-play-fill text-white text-xl"></i>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-file-text-line text-6xl text-gray-300"></i>
                      </div>
                    )}
                  </div>

                  {/* 콘텐츠 */}
                  <div className="p-5">
                    {/* 카테고리 뱃지 */}
                    <div className="mb-3">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          categoryColors[resource.category as keyof typeof categoryColors] ||
                          'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {resource.category}
                      </span>
                    </div>

                    {/* 제목 */}
                    <h3 className="text-base font-semibold text-gray-900 line-clamp-2 mb-2 min-h-[3rem]">
                      {resource.title}
                    </h3>

                    {/* 설명 */}
                    {resource.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">{resource.description}</p>
                    )}

                    {/* 날짜 */}
                    {resource.published_at && (
                      <p className="text-xs text-gray-400 mb-3">{formatDate(resource.published_at)}</p>
                    )}

                    {/* 액션 버튼 */}
                    {resource.url && (
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="flex items-center text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-external-link-line mr-1.5"></i>
                        <span>자세히 보기</span>
                      </a>
                    )}

                    {resource.file_url && (
                      <button
                        onClick={() =>
                          handleDownload(resource.file_url!, resource.file_name || 'download')
                        }
                        className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-orange-50 text-orange-600 rounded-lg text-sm font-semibold hover:bg-orange-100 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-download-line"></i>
                        다운로드
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* 전체보기 버튼 */}
            <div className="text-center">
              <button
                onClick={handleViewAll}
                className="inline-flex items-center space-x-2 bg-orange-600 text-white px-8 py-3.5 rounded-full text-base font-semibold hover:bg-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                <span>전체보기</span>
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}